import { useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles, Save, Send, NotebookText } from "lucide-react";

/* Resumo pós-reunião: escrever à mão ou pedir um rascunho à IA, e enviar a quem faltou. */
export default function ConvocationSummaryPanel({ conv, onDone }) {
  const [text, setText] = useState(conv.summary || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(null);
  const [audience, setAudience] = useState("all");

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (err) { toast.error(formatApiError(err)); }
    setBusy(null);
  };
  const draft = () => run("ai", async () => {
    const r = await api.post(`/convocations/${conv.id}/summary/draft`, { notes });
    setText(r.data.summary);
    toast.success("Rascunho pronto — revê antes de enviar");
  });
  const save = () => run("save", async () => {
    await api.put(`/convocations/${conv.id}/summary`, { summary: text });
    toast.success("Resumo guardado");
    onDone();
  });
  const send = () => run("send", async () => {
    await api.put(`/convocations/${conv.id}/summary`, { summary: text });
    const r = await api.post(`/convocations/${conv.id}/summary/send?audience=${audience}`);
    toast.success(`Resumo para ${r.data.recipients} pessoas · ${r.data.emails} emails enviados`);
    onDone();
  });

  return (
    <div className="border-t border-[#161616] p-5 space-y-4" data-testid={`summary-box-${conv.id}`}>
      <div className="flex items-center gap-2">
        <NotebookText className="w-4 h-4 text-[#EAB308]" />
        <div className="kw-overline flex-1">Resumo pós-reunião</div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="summary-audience">
        {[["all", `A todos os convocados (${conv.total_count ?? 0})`], ["absent", `Só a quem faltou (${conv.absent_count ?? 0})`]].map(([v, label]) => (
          <button key={v} onClick={() => setAudience(v)}
            className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors ${audience === v ? "border-[#EAB308] text-[#0A0A0A] bg-[#EAB308]" : "border-[#333] text-[#A3A3A3] hover:text-white"}`}
            data-testid={`summary-audience-${v}`}>
            {label}
          </button>
        ))}
      </div>
      {audience === "absent" && !conv.attendance_closed_at && (
        <p className="text-xs text-[#EAB308]" data-testid="summary-no-attendance">
          Ainda não fizeste a chamada de presenças — sem ela, todos contam como ausentes.
        </p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="kw-overline text-[10px] block mb-1">Notas rápidas para a IA (opcional)</label>
          <textarea className="kw-input min-h-[140px]" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex.: apresentámos as regras; reporting até domingo 23:59; próxima reunião dia 22…"
            data-testid="summary-notes" />
          <button onClick={draft} disabled={!!busy || notes.trim().length < 5}
            className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5 mt-2"
            data-testid="summary-ai-draft">
            <Sparkles className="w-3 h-3" /> {busy === "ai" ? "A redigir…" : "Redigir com IA"}
          </button>
        </div>
        <div>
          <label className="kw-overline text-[10px] block mb-1">Resumo que recebem</label>
          <textarea className="kw-input min-h-[140px]" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Escreve aqui o resumo da reunião…" data-testid="summary-text" />
          <div className="flex flex-wrap gap-2 mt-2">
            <button onClick={save} disabled={!!busy || text.trim().length < 10}
              className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5" data-testid="summary-save">
              <Save className="w-3 h-3" /> Guardar
            </button>
            <button onClick={send} disabled={!!busy || text.trim().length < 10}
              className="kw-btn-primary !py-2 text-[10px] inline-flex items-center gap-1.5" data-testid="summary-send">
              <Send className="w-3 h-3" /> {busy === "send" ? "A enviar…" : "Enviar resumo"}
            </button>
          </div>
          {conv.summary_sent_at && (
            <p className="text-[10px] uppercase tracking-widest text-[#737373] mt-2" data-testid="summary-sent-info">
              Enviado a {conv.summary_sent_count} pessoas · quem já recebeu não recebe outro email
            </p>
          )}
        </div>
      </div>
      <p className="text-[10px] text-[#737373]">
        Cada pessoa recebe um email com o resumo, o material e as próximas datas, e vê-o no Dashboard.
      </p>
    </div>
  );
}
